import { HStack, Box } from '@terra/ui-styled-system';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import { Badge } from '../../../components/badge';
import { Tooltip } from '../../../components/tooltip';
import { MetaDefault } from '../../../components/typography';
import { TechnologyIcon } from '../../technology-icon/TechnologyIcon';
import { shouldShowUnmatchedInventory } from './inventory-linked';

export type CloudNetblockMetaProps = {
  cloudProvider?: string | null;
  cloudAccountRef?: string | null;
  resourceKind?: string | null;
  inventoryLinked?: boolean;
  cloudAllocationId?: string | null;
  deprovisionedAt?: Date | string | null;
  unmatchedLabel?: string;
  showAccount?: boolean;
};

const formatResourceKind = ({
  resourceKind,
}: {
  resourceKind?: string | null;
}): string | null => {
  if (!resourceKind) {
    return null;
  }

  return resourceKind.replaceAll('_', ' ');
};

export function CloudNetblockMeta({
  cloudProvider,
  cloudAccountRef,
  resourceKind,
  inventoryLinked,
  cloudAllocationId,
  deprovisionedAt,
  unmatchedLabel,
  showAccount = true,
}: CloudNetblockMetaProps) {
  const kindLabel = formatResourceKind({ resourceKind });
  const showUnmatched =
    Boolean(unmatchedLabel) &&
    shouldShowUnmatchedInventory({
      cloudProvider,
      inventoryLinked,
      cloudAllocationId,
      deprovisionedAt,
    });

  if (!showAccount) {
    if (!kindLabel && !showUnmatched) {
      return null;
    }

    return (
      <HStack gap={1.5} alignItems="center" minW={0}>
        {kindLabel ? (
          <Badge variant="outline" size="sm" colorPalette="gray">
            {kindLabel}
          </Badge>
        ) : null}
        {showUnmatched ? (
          <Badge variant="outline" size="sm" colorPalette="orange">
            {unmatchedLabel}
          </Badge>
        ) : null}
      </HStack>
    );
  }

  if (!cloudProvider) {
    return null;
  }

  const providerLabel = formatCloudProviderLabel({ provider: cloudProvider });
  const accountLabel = cloudAccountRef ?? providerLabel;
  const tooltip = [providerLabel, cloudAccountRef, kindLabel]
    .filter(Boolean)
    .join(' · ');

  return (
    <HStack gap={1.5} alignItems="center" minW={0}>
      <Tooltip title={tooltip}>
        <HStack gap={1.5} alignItems="center" minW={0}>
          <Box display="inline-flex" alignItems="center" flexShrink={0}>
            <TechnologyIcon name={cloudProvider} size={16} showInitials />
          </Box>
          <MetaDefault
            color="fg.muted"
            maxW="140px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {kindLabel ? `${accountLabel} · ${kindLabel}` : accountLabel}
          </MetaDefault>
        </HStack>
      </Tooltip>
      {showUnmatched ? (
        <Badge variant="outline" size="sm" colorPalette="orange">
          {unmatchedLabel}
        </Badge>
      ) : null}
    </HStack>
  );
}
